// components/forms/EventSlotCard/index.jsx
import { useMemo } from "react";
import { useEventSlotCreate } from "../../../hooks/useEventSlotCreate";
import { useEventSlotEdit } from "../../../hooks/useEventSlotEdit";
import { useEventSlotFetchData } from "../../../hooks/useEventSlotFetchData";
import { useEventSlotFormData } from "../../../hooks/useEventSlotFormData";
import { useRecurrence } from "../../../hooks/useRecurrence";
import { getEventTypeOptions } from "../../../utils";
import EventSlotLayout from "./EventSlotLayout";
import { useAppMode } from "../../../context/AppMode";

export default function EventSlotCard({ event, slot, onSuccess, onCancel }) {
  const { mode, currentUser } = useAppMode();

  /* 1. Data */
  const { data, loading } = useEventSlotFetchData(slot?.id, event?.id);

  /* 2. Form state */
  const form = useEventSlotFormData(event, slot);

  /* 3. Recurrence hook */
  const {
    recurrence,
    hasDraft, // Nouveau: indique si des modifications sont en attente
    error: recurrenceError,
    updateDraft,
    saveRecurrence,
    deleteRecurrence,
    createPendingRecurrence,
  } = useRecurrence(event?.id);

  /* 4. Actions */
  const { createEventSlot, submitting: submittingCreate } =
    useEventSlotCreate(onSuccess);

  const { updateEventSlot, submitting: submittingEdit } = useEventSlotEdit(
    event,
    slot,
    onSuccess,
  );

  /* 5. Derived data */
  const displayedParticipants = useMemo(() => {
    if (!data?.allUsers) return [];
    return form.formData.participants
      .map((p) => ({
        ...p,
        users: data.allUsers.find((u) => u.id === p.user_id),
      }))
      .sort((a, b) => {
        const nameA = a.users?.first_name || "";
        const nameB = b.users?.first_name || "";
        return nameA.localeCompare(nameB);
      });
  }, [data, form.formData.participants]);

  const availableUsers = useMemo(() => {
    if (!data?.allUsers) return [];
    const used = displayedParticipants.map((p) => p.user_id);
    return data.allUsers
      .filter(
        (u) =>
          !used.includes(u.id) && !form.formData.instructor_ids.includes(u.id),
      )
      .sort((a, b) => {
        const nameA = a.first_name || "";
        const nameB = b.first_name || "";
        return nameA.localeCompare(nameB);
      });
  }, [data, displayedParticipants, form.formData.instructor_ids]);

  const currentUserId = currentUser;

  /* 6. Submit */
  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (form.isEditing) {
        // En mode édition, sauvegarder la récurrence seulement si elle a été modifiée (hasDraft) ou si elle existe déjà
        const saveRecurrenceFn =
          hasDraft || recurrence?.id
            ? (slotData) => saveRecurrence(slotData)
            : null;

        await updateEventSlot(
          form.formData,
          form.initialInstructorIds,
          form.initialParticipants,
          saveRecurrenceFn,
        );
      } else {
        // En mode création
        await createEventSlot(form.formData, createPendingRecurrence);
      }
    } catch (err) {
      form.setErrors({ submit: err.message });
    }
  };

  return (
    <EventSlotLayout
      loading={loading}
      onCancel={onCancel}
      onSubmit={handleSubmit}
      data={data}
      form={form}
      submitting={submittingCreate || submittingEdit}
      eventTypeOptions={getEventTypeOptions()}
      displayedParticipants={displayedParticipants}
      availableUsers={availableUsers}
      currentUserId={currentUserId}
      recurrence={recurrence}
      onRecurrenceChange={updateDraft}
      onDeleteRecurrence={deleteRecurrence}
      recurrenceError={recurrenceError}
    />
  );
}
